import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, TextField, InputAdornment, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoIcon from '@mui/icons-material/Info';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ClearIcon from "@mui/icons-material/Clear"
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import CreateTicketModal from './Create/CreateTicketModal';
import { kb, kbCategories } from '@/api';
import { useSupportContext } from '@/providers/SupportProvider/SupportProvider';

/* ─────────────────────────────────────────────
   Design tokens — matches HTML CSS exactly
   Primary: rgba(74, 102, 241, 1)  (blue)
───────────────────────────────────────────── */
const T = {
  primary: 'rgba(74, 102, 241, 1)',
  primary90: 'rgba(74, 102, 241, 0.9)',
  primary50: 'rgba(74, 102, 241, 0.5)',
  primary30: 'rgba(74, 102, 241, 0.3)',
  primary20: 'rgba(74, 102, 241, 0.2)',
  primary16: 'rgba(74, 102, 241, 0.16)',
  primary08: 'rgba(74, 102, 241, 0.08)',
  primary04: 'rgba(74, 102, 241, 0.04)',
  bgDefault: '#f5f5f5',
  bgPaper: '#ffffff',
  bgLight: '#FAFAFA',
  textDark: '#1a1a1a',
  textPrimary: '#212121',
  textSecondary: '#616161',
  textMedium: '#424242',
  divider: '#EBEBEB',
  border: '#E0E0E0',
  borderLight: '#EBEBEB',
  warnBg: '#FFF4E5',
  warnBorder: '#FFE0B2',
  warnText: '#5F370E',
  warnMain: '#FF9800',
  error: '#F44336',
  success: '#2E7D32',
  successBg: '#E8F5E9',
};

const HelpGuidesMUI = () => {
  const { createTicket } = useSupportContext();
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'article'
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchVal, setSearchVal] = useState('');
  const [activeArticleId, setActiveArticleId] = useState(null);
  const [activeArticleDetail, setActiveArticleDetail] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);
  const [voteSubmitting, setVoteSubmitting] = useState(false);
  const [userVoted, setUserVoted] = useState(null); // 'yes' | 'no' | null

  /* ── data loading ── */
  const loadData = async (search = '') => {
    try {
      setLoading(true);
      const [articleList, catList] = await Promise.all([
        kb.listArticles(undefined, search).catch(() => []),
        kbCategories.listCategories(undefined, true).catch(() => []),
      ]);

      const toArray = (r) =>
        Array.isArray(r) ? r : Array.isArray(r?.content) ? r.content : Array.isArray(r?.data) ? r.data : [];

      setArticles(toArray(articleList));
      setCategories(toArray(catList));
    } catch (err) {
      console.error('Error loading help guides:', err);
      setArticles([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      await loadData();
      const params = new URLSearchParams(window.location.search);
      const articleId = params.get('articleId') || params.get('id');
      if (articleId) {
        handleOpenArticle(articleId);
      }
    };
    init();
  }, []);

  const handleSearch = (e) => {
    if (e.key === 'Enter') loadData(searchVal);
  };

  /* ── article open/close ── */
  const handleOpenArticle = async (articleId) => {
    try {
      setArticleLoading(true);
      setViewMode('article');
      setActiveArticleId(articleId);
      setUserVoted(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const detail = await kb.getArticleById(undefined, articleId);
      setActiveArticleDetail(detail);
      kb.recordView(undefined, articleId).catch(() => { });
    } catch (err) {
      console.error('Error loading article details:', err);
    } finally {
      setArticleLoading(false);
    }
  };

  const handleCloseArticle = () => {
    setViewMode('list');
    setActiveArticleId(null);
    setActiveArticleDetail(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* ── voting ── */
  const handleVote = async (helpful) => {
    if (voteSubmitting || userVoted || !activeArticleDetail) return;
    try {
      setVoteSubmitting(true);
      const res = await kb.castVote(undefined, activeArticleDetail.id, { helpful });
      if (res) setActiveArticleDetail(res);
      setUserVoted(helpful ? 'yes' : 'no');
    } catch (err) {
      console.error('Error casting vote:', err);
    } finally {
      setVoteSubmitting(false);
    }
  };

  /* ── TOC scroll ── */
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  /* ── grouping & sorting ── */
  const articlesByCategory = articles.reduce((acc, article) => {
    const catName = article.categoryName || 'General Help';
    if (!acc[catName]) acc[catName] = [];
    acc[catName].push(article);
    return acc;
  }, {});

  const sortedCategoryNames = Object.keys(articlesByCategory).sort((a, b) => {
    const catA = categories.find((c) => c.name === a);
    const catB = categories.find((c) => c.name === b);
    return ((catA?.order ?? 999) - (catB?.order ?? 999));
  });

  /* ── markdown renderer — identical to original ── */
  const renderMarkdown = (text) => {
    if (!text) return { __html: '' };
    let html = text
      .replace(/^### (.*$)/gim, (_, p1) => `<h4 id="${p1.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}" style="font-size:16px;font-weight:700;margin-top:24px;margin-bottom:12px;color:#1a1a1a;">${p1}</h4>`)
      .replace(/^## (.*$)/gim, (_, p1) => `<h3 id="${p1.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}" style="font-size:18px;font-weight:700;margin-top:28px;margin-bottom:12px;color:#1a1a1a;">${p1}</h3>`)
      .replace(/^# (.*$)/gim, (_, p1) => `<h2 id="${p1.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}" style="font-size:22px;font-weight:700;margin-top:32px;margin-bottom:16px;color:#1a1a1a;">${p1}</h2>`)
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code style="background:#FAFAFA;padding:2px 6px;border-radius:4px;font-family:monospace;font-size:90%;">$1</code>');

    const lines = html.split('\n');
    let inList = false, listType = null, processedLines = [];
    lines.forEach((line) => {
      const trimmed = line.trim();
      const isBullet = trimmed.startsWith('- ') || trimmed.startsWith('* ');
      const numMatch = trimmed.match(/^(\d+)\.\s(.*)/);
      if (isBullet) {
        if (!inList || listType !== 'ul') {
          if (inList) processedLines.push(`</${listType}>`);
          processedLines.push('<ul style="margin-left:20px;margin-bottom:16px;list-style-type:disc;">');
          inList = true; listType = 'ul';
        }
        processedLines.push(`<li style="margin-bottom:8px;line-height:1.6;color:#424242;">${trimmed.substring(2)}</li>`);
      } else if (numMatch) {
        if (!inList || listType !== 'ol') {
          if (inList) processedLines.push(`</${listType}>`);
          processedLines.push('<ol style="margin-left:20px;margin-bottom:16px;list-style-type:decimal;">');
          inList = true; listType = 'ol';
        }
        processedLines.push(`<li style="margin-bottom:8px;line-height:1.6;color:#424242;">${numMatch[2]}</li>`);
      } else {
        if (inList) { processedLines.push(`</${listType}>`); inList = false; listType = null; }
        if (trimmed === '') {
          processedLines.push('<br />');
        } else if (trimmed.startsWith('<h') || trimmed.startsWith('<br')) {
          processedLines.push(trimmed);
        } else {
          processedLines.push(`<p style="font-size:15px;color:#424242;line-height:1.6;margin-bottom:16px;">${trimmed}</p>`);
        }
      }
    });
    if (inList) processedLines.push(`</${listType}>`);
    return { __html: processedLines.join('\n') };
  };

  /* ── TOC headers from article body ── */
  const getHeadersFromBody = (bodyText) => {
    if (!bodyText) return [];
    return bodyText.split('\n').reduce((acc, line) => {
      const match = line.match(/^(##|###)\s(.*)/);
      if (match) acc.push({
        level: match[1].length,
        text: match[2].trim(),
        id: match[2].trim().toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      });
      return acc;
    }, []);
  };

  /* ── related articles ── */
  const relatedArticles = activeArticleDetail
    ? articles.filter((a) => a.categoryId === activeArticleDetail.categoryId && a.id !== activeArticleDetail.id).slice(0, 3)
    : [];

  const helpfulVotes = activeArticleDetail?.helpfulVotes || 0;
  const notHelpfulVotes = activeArticleDetail?.notHelpfulVotes || 0;
  const totalVotes = helpfulVotes + notHelpfulVotes;
  const displayPercent = totalVotes > 0 ? Math.round((helpfulVotes / totalVotes) * 100) : 0;

  const handleCreateTicketSubmit = async (payload) => await createTicket(payload);

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <>
      <style>{`
        html { scroll-behavior: smooth; }
        @keyframes helpFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        #helpListMode, #helpArticleMode { animation: helpFadeIn 0.32s ease-out; }
      `}</style>

      {/* ── outer shell — no extra background, no padding beyond what HTML has ── */}
      <Box sx={{ width: '100%', minHeight: '100vh' }}>
        <Box sx={{ padding: '32px', display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* ── 3-column grid: nav | main | toc ── */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '220px minmax(0, 1fr) 200px',
            gap: '40px',
            alignItems: 'start',
          }}>

            {/* ══════════════════════════════════
                LEFT NAV  (help-docs-nav)
            ══════════════════════════════════ */}
            <Box
              component="aside"
              aria-label="Help center navigation"
              sx={{
                position: 'sticky',
                top: '4px',
                maxHeight: 'calc(100vh - 64px - 32px)',
                overflowY: 'auto',
                pr: '8px',
                fontSize: '13.5px',
                '& h4': {
                  fontFamily: 'inherit',
                  fontSize: '13px',
                  fontWeight: 700,
                  color: T.textDark,
                  mt: '20px',
                  mb: '6px',
                  letterSpacing: 0,
                },
                '& h4:first-of-type': { mt: 0 },
                '& ul': { listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column' },
                '& li': { m: 0 },
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} sx={{ color: T.primary }} />
                </Box>
              ) : sortedCategoryNames.length === 0 ? (
                <Typography sx={{ fontSize: '13px', color: T.textSecondary, p: '6px 8px' }}>
                  No guides available.
                </Typography>
              ) : (
                sortedCategoryNames.map((catName, idx) => (
                  <React.Fragment key={catName}>
                    <Typography
                      component="h4"
                      sx={{ mt: idx === 0 ? '0 !important' : undefined }}
                    >
                      {catName}
                    </Typography>
                    <Box component="ul">
                      {articlesByCategory[catName].map((art) => {
                        const isActive = activeArticleId === art.id && viewMode === 'article';
                        return (
                          <Box component="li" key={art.id}>
                            <Box
                              component="span"
                              onClick={() => handleOpenArticle(art.id)}
                              sx={{
                                display: 'block',
                                padding: '5px 8px',
                                color: isActive ? T.primary : T.textSecondary,
                                cursor: 'pointer',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: isActive ? 600 : 400,
                                lineHeight: 1.4,
                                bgcolor: isActive ? T.primary08 : 'transparent',
                                transition: 'all 0.15s',
                                '&:hover': {
                                  bgcolor: T.primary04,
                                  color: T.textPrimary,
                                },
                              }}
                            >
                              {art.title}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </React.Fragment>
                ))
              )}
            </Box>

            {/* ══════════════════════════════════
                CENTER MAIN  (help-docs-main)
            ══════════════════════════════════ */}
            <Box sx={{ minWidth: 0 }}>

              {/* ── top bar: search + create button ── */}
              {/* ── top bar: search + create button ── */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  mb: 2.5,
                  flexWrap: 'wrap',
                }}
              >
                {/* search */}
                <Box
                  sx={{
                    flex: 1,
                    minWidth: { xs: '100%', sm: '240px' },
                    maxWidth: { xs: '100%', md: '420px' },
                  }}
                >
                  <TextField
                    fullWidth
                    type="text"
                    placeholder="Search tickets..."
                    value={searchVal}
                    onChange={(e) => {
                      setSearchVal(e.target.value);

                      if (e.target.value === '') {
                        loadData('');
                      }
                    }}
                    onKeyDown={handleSearch}
                    autoComplete="off"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            onClick={() => loadData(searchVal)}
                            sx={{
                              color: '#AEAEAE',
                              fontSize: 15,
                              cursor: 'pointer',
                            }}
                          />
                        </InputAdornment>
                      ),

                      endAdornment: (
                        <InputAdornment position="end" sx={{ gap: 0.5 }}>
                          {searchVal && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSearchVal('');
                                loadData('');
                              }}
                              sx={{
                                p: '4px',
                              }}
                            >
                              <ClearIcon
                                sx={{
                                  fontSize: '15px',
                                }}
                              />
                            </IconButton>
                          )}

                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => loadData(searchVal)}
                            sx={{
                              minWidth: 'unset',
                              height: 26,
                              px: 1.8,
                              borderRadius: '6px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              textTransform: 'none',
                              boxShadow: 'none',
                            }}
                          >
                            Search
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      bgcolor: '#FAFAFA',

                      '& .MuiOutlinedInput-root': {
                        height: 38,
                        borderRadius: '8px',
                        pr: '6px',
                        fontSize: '0.8125rem',
                        bgcolor: '#FAFAFA',

                        '& fieldset': {
                          borderColor: '#E8E8E8',
                        },

                        '&:hover fieldset': {
                          borderColor: 'rgba(74,102,241,0.4)',
                        },

                        '&.Mui-focused fieldset': {
                          borderColor: 'rgba(74,102,241,1)',
                        },
                      },

                      '& .MuiInputBase-input': {
                        py: '8px',
                        fontSize: '0.8125rem',
                      },
                    }}
                  />
                </Box>

                {/* create ticket button */}
                <Button
                  onClick={() => setOpenModal(true)}
                  startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                  sx={{
                    height: 38,
                    px: 2,
                    borderRadius: '8px',
                    bgcolor: T.primary,
                    color: '#fff',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    boxShadow: 'none',

                    '&:hover': {
                      bgcolor: T.primary90,
                      boxShadow: 'none',
                    },
                  }}
                >
                  Create Ticket
                </Button>
              </Box>
              {/* ══ LIST MODE ══ */}
              {viewMode === 'list' && (
                <Box id="helpListMode">
                  <Typography
                    component="h1"
                    sx={{ fontSize: '2rem', fontWeight: 600, color: T.textDark, m: 0, mb: '6px', letterSpacing: '-0.01em' }}
                  >
                    Help center
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: T.textSecondary, mb: '16px' }}>
                    Last updated: April 2026
                  </Typography>
                  <Typography sx={{ fontSize: '15px', color: T.textPrimary, lineHeight: 1.6, maxWidth: '720px', mb: '32px' }}>
                    Inboxlogy gives cold-email teams the mailboxes, domains, deliverability tools, and API they need to
                    land in the inbox. Find answers fast below, or open a ticket if you need a human on the other side.
                  </Typography>

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress sx={{ color: T.primary }} />
                    </Box>
                  ) : sortedCategoryNames.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <Typography sx={{ color: T.textSecondary, mb: 2 }}>
                        No guides found matching your search.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => { setSearchVal(''); loadData(''); }}
                        sx={{
                          color: T.primary,
                          borderColor: T.primary,
                          textTransform: 'none',
                          '&:hover': { bgcolor: T.primary08, borderColor: T.primary },
                        }}
                      >
                        Clear Search
                      </Button>
                    </Box>
                  ) : (
                    sortedCategoryNames.map((catName) => (
                      <Box
                        key={catName}
                        component="section"
                        sx={{ '& + &': { mt: '40px' } }}
                      >
                        <Typography
                          component="h2"
                          id={`sec-${catName.toLowerCase().replace(/\s+/g, '-')}`}
                          sx={{ fontFamily: 'inherit', fontSize: '22px', fontWeight: 600, color: T.textDark, m: 0, mb: '18px', scrollMarginTop: '16px' }}
                        >
                          {catName}
                        </Typography>

                        {/* article cards */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                          {articlesByCategory[catName].map((art) => (
                            <Box
                              key={art.id}
                              component="a"
                              onClick={() => handleOpenArticle(art.id)}
                              sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                gap: '12px',
                                alignItems: 'flex-start',
                                padding: '2px 0',
                                textDecoration: 'none',
                                '&:hover .doc-article-title': { textDecoration: 'underline' },
                              }}
                            >
                              <ArticleIcon sx={{ color: T.primary, fontSize: '20px', mt: '2px', flexShrink: 0 }} />
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                  className="doc-article-title"
                                  sx={{ color: T.primary, fontSize: '14.5px', fontWeight: 500, lineHeight: 1.4, display: 'inline-block', mb: '4px' }}
                                >
                                  {art.title}
                                </Typography>
                                <Typography sx={{ color: T.textSecondary, fontSize: '13px', lineHeight: 1.5 }}>
                                  {art.summary}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              )}

              {/* ══ ARTICLE MODE ══ */}
              {viewMode === 'article' && (
                <Box id="helpArticleMode">
                  {/* back button */}
                  <Box
                    component="button"
                    onClick={handleCloseArticle}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      color: T.primary,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: '13px',
                      fontWeight: 500,
                      padding: '6px 10px 6px 6px',
                      mb: '14px',
                      ml: '-6px',
                      border: 'none',
                      background: 'transparent',
                      borderRadius: '6px',
                      '&:hover': { bgcolor: T.primary04 },
                    }}
                  >
                    <ArrowBackIcon sx={{ fontSize: '18px' }} />
                    Back to Help center
                  </Box>

                  {articleLoading || !activeArticleDetail ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress sx={{ color: T.primary }} />
                    </Box>
                  ) : (
                    /* article-inline */
                    <Box sx={{ maxWidth: '720px' }}>

                      {/* breadcrumb */}
                      <Typography sx={{ fontSize: '12px', color: T.textSecondary, mb: '10px' }}>
                        <Box
                          component="a"
                          onClick={handleCloseArticle}
                          sx={{ color: T.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                          Help center
                        </Box>
                        {' ›'}&nbsp;
                        <Box component="span" id="articleBreadcrumbCategory">
                          {activeArticleDetail.categoryName || 'General Help'}
                        </Box>
                        {' ›'}&nbsp;
                        <Box component="span" id="articleBreadcrumbTitle">
                          {activeArticleDetail.title}
                        </Box>
                      </Typography>

                      {/* title */}
                      <Typography
                        component="h1"
                        id="articleTitle"
                        sx={{
                          fontFamily: 'inherit',
                          fontSize: '1.9rem',
                          fontWeight: 600,
                          color: T.textDark,
                          m: 0,
                          mb: '8px',
                          letterSpacing: '-0.01em',
                          lineHeight: 1.25,
                        }}
                      >
                        {activeArticleDetail.title}
                      </Typography>

                      {/* meta */}
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '12px',
                          color: T.textSecondary,
                          fontWeight: 500,
                          mt: '4px',
                          mb: '24px',
                        }}
                      >
                        <AccessTimeIcon sx={{ fontSize: '14px' }} />
                        {activeArticleDetail.publishedAt
                          ? new Date(activeArticleDetail.publishedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                          : 'Recently updated'}
                        &nbsp;·&nbsp;{activeArticleDetail.viewCount || 0} views
                      </Box>

                      {/* body */}
                      <Box
                        sx={{
                          '& h2': { fontFamily: 'inherit', fontSize: '22px', color: T.textDark, fontWeight: 600, mt: '32px', mb: '10px', scrollMarginTop: '16px' },
                          '& h3': { fontFamily: 'inherit', fontSize: '17px', color: T.textDark, fontWeight: 600, mt: '28px', mb: '10px', scrollMarginTop: '16px' },
                          '& h4': { fontFamily: 'inherit', fontSize: '15px', color: T.textDark, fontWeight: 600, mt: '24px', mb: '8px' },
                          '& p': { fontSize: '14px', lineHeight: 1.65, color: T.textPrimary, m: 0, mb: '14px' },
                          '& ol, & ul': { paddingLeft: '20px', lineHeight: 1.8, fontSize: '14px', color: T.textPrimary, mb: '18px' },
                        }}
                        dangerouslySetInnerHTML={renderMarkdown(activeArticleDetail.body)}
                      />

                      {/* callout */}
                      <Box
                        id="a-email-tips"
                        sx={{
                          bgcolor: T.warnBg,
                          border: `1px solid ${T.warnBorder}`,
                          color: T.warnText,
                          borderRadius: '10px',
                          p: '14px 16px',
                          my: '22px',
                          fontSize: '13.5px',
                          lineHeight: 1.55,
                          scrollMarginTop: '16px',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, mb: '4px' }}>
                          <InfoIcon sx={{ fontSize: '18px' }} />
                          Relevant tips
                        </Box>
                        <Typography sx={{ color: T.warnText, fontSize: '14px', lineHeight: 1.5 }}>
                          If you require additional support related to {activeArticleDetail.title}, consider opening a
                          detailed support ticket. Let our L2 team assist you.
                        </Typography>
                      </Box>

                      {/* feedback */}
                      <Box sx={{ mt: '32px', pt: '20px', borderTop: `1px solid ${T.divider}` }}>
                        <Typography sx={{ fontSize: '13px', color: T.textSecondary, mb: '8px' }}>
                          Was this article helpful?
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {/* thumbs up */}
                          <Box
                            component="button"
                            onClick={() => handleVote(true)}
                            disabled={voteSubmitting || userVoted !== null}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '5px 12px',
                              borderRadius: '8px',
                              border: `1px solid ${userVoted === 'yes' ? T.primary : T.border}`,
                              background: userVoted === 'yes' ? T.primary08 : 'transparent',
                              color: userVoted === 'yes' ? T.primary : T.textSecondary,
                              fontFamily: 'inherit',
                              fontSize: '12px',
                              fontWeight: 500,
                              cursor: voteSubmitting || userVoted ? 'default' : 'pointer',
                              opacity: voteSubmitting || (userVoted && userVoted !== 'yes') ? 0.5 : 1,
                              transition: 'all 0.2s',
                              '&:hover:not(:disabled)': { bgcolor: T.primary08 },
                            }}
                          >
                            <ThumbUpOutlinedIcon sx={{ fontSize: '16px' }} /> Yes
                          </Box>

                          {/* thumbs down */}
                          <Box
                            component="button"
                            onClick={() => handleVote(false)}
                            disabled={voteSubmitting || userVoted !== null}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '5px 12px',
                              borderRadius: '8px',
                              border: `1px solid ${userVoted === 'no' ? T.error : T.border}`,
                              background: userVoted === 'no' ? 'rgba(244,67,54,0.08)' : 'transparent',
                              color: userVoted === 'no' ? T.error : T.textSecondary,
                              fontFamily: 'inherit',
                              fontSize: '12px',
                              fontWeight: 500,
                              cursor: voteSubmitting || userVoted ? 'default' : 'pointer',
                              opacity: voteSubmitting || (userVoted && userVoted !== 'no') ? 0.5 : 1,
                              transition: 'all 0.2s',
                              '&:hover:not(:disabled)': { bgcolor: 'rgba(244,67,54,0.08)' },
                            }}
                          >
                            <ThumbDownOutlinedIcon sx={{ fontSize: '16px' }} /> No
                          </Box>

                          <Typography
                            component="span"
                            sx={{ ml: '12px', fontSize: '12px', color: T.textSecondary }}
                          >
                            {totalVotes > 0 ? `${displayPercent}% found this helpful` : 'Be the first to vote!'}
                            {' '}({totalVotes} votes)
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* ══════════════════════════════════
                RIGHT TOC  (help-docs-toc)
            ══════════════════════════════════ */}
            <Box
              component="aside"
              aria-label="On this page"
              sx={{
                position: 'sticky',
                top: '4px',
                maxHeight: 'calc(100vh - 64px - 32px)',
                overflowY: 'auto',
                fontSize: '13px',
              }}
            >
              <Typography
                component="h5"
                sx={{
                  fontFamily: 'inherit',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: T.textSecondary,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  m: 0,
                  mb: '12px',
                }}
              >
                On this page
              </Typography>

              {/* list mode TOC */}
              {viewMode === 'list' && (
                <Box component="ul" id="helpTocList" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {sortedCategoryNames.map((catName) => (
                    <Box component="li" key={catName}>
                      <Box
                        component="span"
                        onClick={() => scrollToSection(`sec-${catName.toLowerCase().replace(/\s+/g, '-')}`)}
                        sx={{
                          color: T.textSecondary,
                          textDecoration: 'none',
                          cursor: 'pointer',
                          display: 'block',
                          padding: '2px 0',
                          fontSize: '13px',
                          lineHeight: 1.4,
                          '&:hover': { color: T.primary },
                        }}
                      >
                        {catName}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* article mode TOC */}
              {viewMode === 'article' && activeArticleDetail && (
                <Box component="ul" id="helpTocArticle" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {getHeadersFromBody(activeArticleDetail.body).map((header, idx) => (
                    <Box component="li" key={idx}>
                      <Box
                        component="span"
                        onClick={() => scrollToSection(header.id)}
                        sx={{
                          color: T.textSecondary,
                          cursor: 'pointer',
                          display: 'block',
                          padding: '2px 0',
                          fontSize: '13px',
                          lineHeight: 1.4,
                          pl: header.level === 3 ? '12px' : 0,
                          '&:hover': { color: T.primary },
                        }}
                      >
                        {header.text}
                      </Box>
                    </Box>
                  ))}
                  <Box component="li">
                    <Box
                      component="span"
                      onClick={() => scrollToSection('a-email-tips')}
                      sx={{
                        color: T.textSecondary,
                        cursor: 'pointer',
                        display: 'block',
                        padding: '2px 0',
                        fontSize: '13px',
                        '&:hover': { color: T.primary },
                      }}
                    >
                      Relevant tips
                    </Box>
                  </Box>
                </Box>
              )}

              {/* article extras */}
              {viewMode === 'article' && activeArticleDetail && (
                <Box id="helpArticleExtras" sx={{ mt: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

                  {/* related articles card */}
                  {relatedArticles.length > 0 && (
                    <Box sx={{
                      bgcolor: T.bgPaper,
                      border: `1px solid ${T.borderLight}`,
                      borderRadius: '12px',
                      p: '14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}>
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: T.textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase', mb: '6px' }}>
                        Related articles
                      </Typography>
                      {relatedArticles.map((art) => (
                        <Box
                          key={art.id}
                          onClick={() => handleOpenArticle(art.id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            p: '7px 8px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            color: T.textDark,
                            fontSize: '13px',
                            fontWeight: 500,
                            lineHeight: 1.35,
                            textDecoration: 'none',
                            transition: 'background 0.15s',
                            '&:hover': { bgcolor: T.primary04, color: T.primary },
                          }}
                        >
                          <ArticleIcon sx={{ color: T.primary, fontSize: '18px', flexShrink: 0 }} />
                          <Box component="span">{art.title}</Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* still stuck CTA card */}
                  <Box sx={{
                    bgcolor: T.primary04,
                    border: `1px solid ${T.primary30}`,
                    borderRadius: '12px',
                    p: '14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}>
                    <Typography sx={{ color: T.primary, fontSize: '13px', fontWeight: 600, mb: '4px' }}>
                      Still stuck?
                    </Typography>
                    <Typography sx={{ color: T.textSecondary, fontSize: '12px', lineHeight: 1.4, mb: '10px' }}>
                      The team will jump in as soon as possible.
                    </Typography>
                    <Box
                      component="button"
                      onClick={() => setOpenModal(true)}
                      sx={{
                        alignSelf: 'flex-start',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '5px 12px',
                        bgcolor: T.primary,
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontFamily: 'inherit',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        boxShadow: `0px 4px 12px ${T.primary30}`,
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: T.primary90 },
                      }}
                    >
                      <AddIcon sx={{ fontSize: '16px' }} />
                      Create ticket
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

          </Box>
        </Box>
      </Box>

      {openModal && (
        <CreateTicketModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          onSubmit={handleCreateTicketSubmit}
        />
      )}
    </>
  );
};

export default HelpGuidesMUI;